import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useOrgFeatures } from '../hooks/useOrgFeatures';
import { Link } from 'react-router-dom';
import { AlertTriangle, CreditCard, Euro, Megaphone } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from 'recharts';

function StatCard({ label, value, sub, color = 'indigo' }) {
  const colors = {
    indigo: 'from-indigo-500/10 to-indigo-600/5 border-indigo-500/20',
    green: 'from-emerald-500/10 to-emerald-600/5 border-emerald-500/20',
    red: 'from-red-500/10 to-red-600/5 border-red-500/20',
    yellow: 'from-yellow-500/10 to-yellow-600/5 border-yellow-500/20',
  };
  return (
    <div className={`bg-gradient-to-br ${colors[color]} border rounded-xl p-5`}>
      <p className="text-gray-400 text-sm">{label}</p>
      <p className="text-white text-3xl font-bold mt-1">{value}</p>
      {sub && <p className="text-gray-500 text-xs mt-1">{sub}</p>}
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quota, setQuota] = useState(null);
  const { subscriptionStatus, planKey, loading: featuresLoading } = useOrgFeatures();

  const isBlocked = !featuresLoading && (
    subscriptionStatus === 'canceled' || subscriptionStatus === 'suspended'
  );
  const hasNoPlan = !featuresLoading && !planKey && subscriptionStatus !== 'canceled';

  useEffect(() => {
    api.get('/api/analytics/overview?days=30')
      .then(r => setStats(r.data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
    api.get('/api/analytics/quota-status')
      .then(r => setQuota(r.data))
      .catch(() => setQuota(null));
  }, []);

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-800 rounded w-1/3" />
          <div className="grid grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => <div key={i} className="h-28 bg-gray-800 rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  const totalSearches = stats?.total_searches ?? 0;
  const noResults = stats?.searches_no_results ?? 0;
  const clicks = stats?.total_clicks ?? 0;
  const ctr = totalSearches > 0 ? ((clicks / totalSearches) * 100).toFixed(1) : '0.0';

  return (
    <div className="p-8">
      {/* Subscription blocked banner */}
      {isBlocked && (
        <div
          data-testid="subscription-blocked-banner"
          className="mb-6 flex items-start gap-4 bg-red-950/60 border border-red-500/40 rounded-xl p-5"
        >
          <AlertTriangle size={22} className="text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-red-300 font-semibold text-sm">Suscripción cancelada</p>
            <p className="text-red-400/80 text-xs mt-1">
              Tu suscripción ha sido cancelada. El widget de búsqueda está desactivado para tus clientes.
              Activa un plan para volver a tener el buscador en tu tienda.
            </p>
          </div>
          <Link
            to="/billing"
            data-testid="subscription-blocked-cta"
            className="flex items-center gap-2 bg-red-500 hover:bg-red-400 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors flex-shrink-0"
          >
            <CreditCard size={14} />
            Activar plan
          </Link>
        </div>
      )}

      {/* No plan yet — soft reminder */}
      {hasNoPlan && (
        <div
          data-testid="no-plan-banner"
          className="mb-6 flex items-start gap-4 bg-yellow-950/40 border border-yellow-500/30 rounded-xl p-5"
        >
          <AlertTriangle size={20} className="text-yellow-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-yellow-300 font-semibold text-sm">Sin plan activo</p>
            <p className="text-yellow-400/70 text-xs mt-1">
              Estás en el período de prueba. Activa un plan para mantener el widget operativo.
            </p>
          </div>
          <Link
            to="/billing"
            data-testid="no-plan-cta"
            className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-400 text-gray-900 text-xs font-semibold px-4 py-2 rounded-lg transition-colors flex-shrink-0"
          >
            <CreditCard size={14} />
            Ver planes
          </Link>
        </div>
      )}

      {/* Quota 80% warning banner */}
      {quota?.warning && (
        <div
          data-testid="quota-warning-banner"
          className="mb-6 flex items-start gap-4 bg-orange-950/40 border border-orange-500/30 rounded-xl p-5"
        >
          <AlertTriangle size={20} className="text-orange-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-orange-300 font-semibold text-sm">Cerca del límite de uso del buscador</p>
            <p className="text-orange-400/70 text-xs mt-1">
              Búsqueda clásica: {quota.classic.used_today}/{quota.classic.limit_daily || '∞'} hoy
              ({quota.classic.pct_daily}%) · IA: {quota.ai.used_today}/{quota.ai.limit_daily || '∞'} hoy
              ({quota.ai.pct_daily}%). Contacta con soporte si necesitas ampliar tu plan.
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <p className="text-gray-400 mt-1">Últimos 30 días</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
        <StatCard
          label="Búsquedas totales"
          value={totalSearches.toLocaleString()}
          sub="últimos 30 días"
          color="indigo"
        />
        <StatCard
          label="Sin resultados"
          value={noResults.toLocaleString()}
          sub={totalSearches > 0 ? `${((noResults / totalSearches) * 100).toFixed(1)}% del total` : '—'}
          color="red"
        />
        <StatCard
          label="Clicks en productos"
          value={clicks.toLocaleString()}
          sub="desde el widget"
          color="green"
        />
        <StatCard
          label="CTR"
          value={`${ctr}%`}
          sub="clicks / búsquedas"
          color="yellow"
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Daily searches chart */}
        <div className="xl:col-span-2 bg-gray-900 border border-gray-800 rounded-xl p-6">
          <h2 className="text-white font-semibold mb-4">Búsquedas diarias</h2>
          {stats?.daily_searches?.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={stats.daily_searches}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 11 }} tickFormatter={d => d.slice(5)} />
                <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} />
                <Tooltip
                  contentStyle={{ background: '#111827', border: '1px solid #374151', borderRadius: '8px' }}
                  labelStyle={{ color: '#e5e7eb' }}
                  itemStyle={{ color: '#818cf8' }}
                />
                <Bar dataKey="count" fill="#6366f1" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex flex-col items-center justify-center h-48 text-gray-600">
              <div className="text-4xl mb-3">🔍</div>
              <p>Sin datos todavía.</p>
              <p className="text-sm mt-1">Instala el widget para empezar a registrar búsquedas.</p>
            </div>
          )}
        </div>

        {/* Top searches */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
          <h2 className="text-white font-semibold mb-4">Top búsquedas</h2>
          {stats?.top_terms?.length > 0 ? (
            <div className="space-y-2">
              {stats.top_terms.slice(0, 8).map((t, i) => (
                <div key={t.term} className="flex items-center gap-3">
                  <span className="text-gray-600 text-xs w-4">{i + 1}</span>
                  <span className="text-gray-300 text-sm flex-1 truncate">{t.term}</span>
                  <span className="text-indigo-400 text-sm font-medium">{t.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-32 text-gray-600 text-sm">
              Sin búsquedas registradas
            </div>
          )}
        </div>
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        {/* No results terms */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
          <h2 className="text-white font-semibold mb-4">🚫 Búsquedas sin resultados</h2>
          {stats?.no_results_terms?.length > 0 ? (
            <div className="space-y-2">
              {stats.no_results_terms.slice(0, 8).map((t) => (
                <div key={t.term} className="flex items-center gap-3">
                  <span className="text-gray-300 text-sm flex-1 truncate">{t.term}</span>
                  <span className="text-red-400 text-sm font-medium">{t.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-24 text-gray-600 text-sm">
              Sin búsquedas sin resultados 🎉
            </div>
          )}
        </div>

        {/* Top clicked products */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
          <h2 className="text-white font-semibold mb-4">🛍️ Productos más clickados</h2>
          {stats?.top_products?.length > 0 ? (
            <div className="space-y-2">
              {stats.top_products.slice(0, 8).map((p) => (
                <div key={p.product_id} className="flex items-center gap-3">
                  <span className="text-gray-300 text-sm flex-1 truncate">{p.title}</span>
                  <span className="text-emerald-400 text-sm font-medium">{p.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-24 text-gray-600 text-sm">
              Sin clicks registrados todavía
            </div>
          )}
        </div>
      </div>

      {/* Popup coupon revenue */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mt-6">
        <div
          data-testid="popup-revenue-summary-card"
          className="xl:col-span-2 bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-500/20 rounded-xl p-6"
        >
          <h2 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Euro size={16} className="text-emerald-400" /> Ingresos por cupones (Popups)
          </h2>
          <div className="flex items-center gap-8">
            <div>
              <p className="text-gray-400 text-sm">Ingresos generados</p>
              <p data-testid="popup-revenue-total" className="text-white text-3xl font-bold mt-1">
                {(stats?.popup_revenue_total ?? 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€
              </p>
            </div>
            <div className="h-12 w-px bg-emerald-500/20" />
            <div>
              <p className="text-gray-400 text-sm">Pedidos con cupón</p>
              <p data-testid="popup-orders-count" className="text-white text-3xl font-bold mt-1">
                {(stats?.popup_orders_count ?? 0).toLocaleString()}
              </p>
            </div>
          </div>
          <p className="text-gray-500 text-xs mt-4">
            Últimos 30 días · atribuido por cupón usado en un pedido real (PrestaShop/Shopify)
          </p>
          {!stats?.popup_campaigns_exist && (
            <Link
              to="/popups"
              data-testid="popup-revenue-create-cta"
              className="inline-flex items-center gap-2 mt-4 text-emerald-400 hover:text-emerald-300 text-xs font-medium"
            >
              <Megaphone size={13} /> Aún no tienes ningún popup — crea el primero
            </Link>
          )}
        </div>

        {/* Top campaigns by revenue */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
          <h2 className="text-white font-semibold mb-4">Top campañas por ingresos</h2>
          {stats?.popup_revenue_by_campaign?.length > 0 ? (
            <div className="space-y-3">
              {stats.popup_revenue_by_campaign.map((c) => (
                <div key={c.campaign_id} className="flex items-center gap-3" data-testid={`dashboard-popup-campaign-${c.campaign_id}`}>
                  <span className="text-gray-300 text-sm flex-1 truncate">{c.name}</span>
                  <span className="text-emerald-400 text-sm font-medium">
                    {c.revenue.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-24 text-gray-600 text-sm text-center px-2">
              Sin ingresos atribuidos todavía
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
